import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, X, Menu } from 'lucide-react';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

/* ─── tokens ─── */
const C = {
  grad: 'linear-gradient(152deg,#061F8E 0%,#0A46E0 58%,#1763F5 100%)',
  blue: '#0A46E0', blueLight: '#1E72FF',
  yellow: '#FCD116',
  stripe: 'repeating-linear-gradient(90deg,#E0319A 0 90px,#FF6A1A 90px 180px,#8FD03A 180px 270px,#2BC0C2 270px 360px)',
  vStripe: 'linear-gradient(180deg,#E0319A,#FF6A1A 35%,#8FD03A 68%,#2BC0C2)',
  ink: '#0F172A', body: '#475569', border: '#E2E8F0',
};

const LOGO_WHITE = 'https://customer-assets.emergentagent.com/job_reshape-website/artifacts/frfzyqk8_20220617%20Reshapers%20Black%20Logo.png';
const LOGO_BLACK = LOGO_WHITE;

const BASE_NAV_ITEMS = [
  { label: 'Home', to: '/' },
  { label: 'Solutions', to: '/solutions' },
  { label: 'Impact', to: '/impact' },
  { label: 'Insights', to: '/insights' },
  { label: 'Open Sessions', to: '/workshops' },
  { label: 'About', to: '/about' },
];

const QUESTIONS = [
  { pre: 'If Curaçao can qualify, what\'s ', hl: 'stopping your transformation', post: '?' },
  { pre: 'If a small island can unite, what could ', hl: 'your culture', post: ' become?' },
  { pre: 'If 156K people can build momentum, what\'s ', hl: 'your team', post: ' capable of?' },
  { pre: 'If Curaçao found its voice, has ', hl: 'your brand', post: ' found yours?' },
  { pre: 'If belief can change the game, what happens when ', hl: 'leaders lead differently', post: '?' },
  { pre: 'If the smallest nation can make history, what\'s ', hl: 'your next chapter', post: '?' },
];

/* ─── announcement bar ─── */
export const BlueWaveBar = () => null; // Removed — only catalyst stripe remains

/* ─── navbar ─── */
const BlueWaveNav = ({ scrolled, pathname, settings }) => {
  const [open, setOpen] = useState(false);
  const s = scrolled;

  // Build nav items dynamically based on feature toggles
  const NAV_ITEMS = [...BASE_NAV_ITEMS];
  if (settings?.tools_library_enabled) NAV_ITEMS.splice(4, 0, { label: 'Tools', to: '/tools' });
  if (settings?.forces_enabled) NAV_ITEMS.splice(NAV_ITEMS.length - 1, 0, { label: 'FORCES', to: 'https://forces.reshapers.co', external: true });
  return (
    <>
      <nav
        className="fixed z-50 flex items-center justify-between transition-all duration-[240ms] ease-out"
        style={{
          top: 28,
          left: 'clamp(14px,4vw,40px)',
          right: 'clamp(14px,4vw,40px)',
          borderRadius: 999,
          padding: '11px 13px 11px 24px',
          background: s ? '#fff' : 'rgba(255,255,255,0.10)',
          border: `1px solid ${s ? C.border : 'rgba(255,255,255,0.22)'}`,
          backdropFilter: 'blur(18px)',
          boxShadow: s ? '0 12px 30px rgba(15,23,42,0.08)' : '0 12px 40px rgba(7,42,174,0.22)',
        }}
      >
        {/* wordmark */}
        <Link to="/" className="flex-shrink-0">
          <img src={LOGO_BLACK} alt="Reshapers" className="h-[25px] transition-all duration-[240ms]" style={{ filter: s ? 'none' : 'brightness(0) invert(1)' }} />
        </Link>

        {/* desktop links */}
        <div className="hidden lg:flex items-center gap-[2px] mx-6">
          {NAV_ITEMS.map(n => {
            const active = pathname === n.to;
            const cls = "text-sm font-medium px-[15px] py-[9px] rounded-full transition-all duration-[240ms]";
            const sty = {
              color: s ? (active ? C.blue : C.body) : (active ? '#fff' : 'rgba(255,255,255,0.82)'),
              background: active ? (s ? '#EAF0FF' : 'rgba(255,255,255,0.16)') : 'transparent',
            };
            return n.external
              ? <a key={n.to} href={n.to} target="_blank" rel="noopener noreferrer" className={cls} style={sty}>{n.label}</a>
              : <Link key={n.to} to={n.to} className={cls} style={sty}>{n.label}</Link>;
          })}
        </div>

        {/* right side */}
        <div className="hidden lg:flex items-center gap-3">
          <Link to="/contact" className="text-sm font-semibold px-[22px] py-[12px] rounded-full transition-all duration-[240ms]" style={{
            background: s ? `linear-gradient(135deg,${C.blue},${C.blueLight})` : '#fff',
            color: s ? '#fff' : C.blue,
            boxShadow: s ? '0 10px 24px rgba(10,70,224,0.32)' : '0 4px 16px rgba(0,0,0,0.1)',
          }}>Let's talk <span className="ml-1">→</span></Link>
        </div>

        {/* hamburger */}
        <button className="lg:hidden p-2" onClick={() => setOpen(true)}>
          <Menu size={22} style={{ color: s ? C.ink : '#fff' }} />
        </button>
      </nav>

      {/* mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-[60] bg-black/50" onClick={() => setOpen(false)}>
          <div className="absolute top-0 right-0 w-72 h-full bg-white shadow-2xl p-6" onClick={e => e.stopPropagation()}>
            <button className="absolute top-4 right-4 text-slate-400 hover:text-slate-800" onClick={() => setOpen(false)}><X size={22} /></button>
            <div className="mt-12 flex flex-col gap-1">
              {NAV_ITEMS.map(n => n.external
                ? <a key={n.to} href={n.to} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} className="text-base font-medium px-4 py-3 rounded-xl hover:bg-slate-50 transition-colors" style={{ color: C.ink }}>{n.label}</a>
                : <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="text-base font-medium px-4 py-3 rounded-xl hover:bg-slate-50 transition-colors" style={{ color: pathname === n.to ? C.blue : C.ink }}>{n.label}</Link>
              )}
              <div className="mt-4 pt-4 border-t border-slate-100">
                <Link to="/contact" onClick={() => setOpen(false)} className="flex items-center justify-center gap-2 w-full py-3 rounded-full text-white font-bold text-sm" style={{ background: `linear-gradient(135deg,${C.blue},${C.blueLight})` }}>Let's talk <ArrowRight size={16} /></Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/* ─── hero ─── */
export const BlueWaveHero = () => {
  const [scrolled, setScrolled] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);
  const [qIdx, setQIdx] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', fn, { passive: true });
    fn();
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/settings`).then(r => r.ok ? r.json() : null).then(d => { if (d) setSiteSettings(d); }).catch(() => {});
  }, []);

  useEffect(() => {
    const q = QUESTIONS[qIdx];
    const fullText = q.pre + q.hl + q.post;
    let timeout;
    if (!isDeleting) {
      if (displayText.length < fullText.length) {
        timeout = setTimeout(() => setDisplayText(fullText.slice(0, displayText.length + 1)), 35);
      } else {
        timeout = setTimeout(() => setIsDeleting(true), 4000);
      }
    } else {
      if (displayText.length > 0) {
        timeout = setTimeout(() => setDisplayText(displayText.slice(0, -1)), 18);
      } else {
        setIsDeleting(false);
        setQIdx(prev => (prev + 1) % QUESTIONS.length);
      }
    }
    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, qIdx]);

  const q = QUESTIONS[qIdx];
  const renderTyped = () => {
    const text = displayText;
    const hlStart = q.pre.length;
    const hlEnd = q.pre.length + q.hl.length;
    if (text.length <= hlStart) {
      return <>{text}<span className="bw-caret" /></>;
    } else if (text.length <= hlEnd) {
      return <>{q.pre}<span style={{ color: C.yellow }}>{text.slice(hlStart)}</span><span className="bw-caret" /></>;
    } else {
      return <>{q.pre}<span style={{ color: C.yellow }}>{q.hl}</span>{text.slice(hlEnd)}<span className="bw-caret" /></>;
    }
  };

  return (
    <>
      <style>{`
        @keyframes bwCaret{0%,100%{opacity:1}50%{opacity:0}}
        .bw-caret{display:inline-block;width:3px;height:0.85em;background:#fff;margin-left:2px;vertical-align:text-bottom;animation:bwCaret 0.8s step-end infinite}
        @keyframes bwM{0%,100%{border-radius:42% 58% 62% 38%/45% 55% 45% 55%}33%{border-radius:55% 45% 38% 62%/58% 42% 58% 42%}66%{border-radius:38% 62% 55% 45%/42% 58% 42% 58%}}
        @keyframes bwD{0%,100%{transform:translate(0,0)}33%{transform:translate(50px,-40px)}66%{transform:translate(-40px,50px)}}
        @keyframes bwD2{0%,100%{transform:translate(0,0)}33%{transform:translate(-60px,30px)}66%{transform:translate(30px,-50px)}}
        @keyframes bwFU{from{opacity:0;transform:translateY(28px)}to{opacity:1;transform:translateY(0)}}
        .bw-fu{animation:bwFU .8s cubic-bezier(.16,1,.3,1) both}
        @media(prefers-reduced-motion:reduce){.bw-blob{animation:none!important}}
      `}</style>

      {/* catalyst stripe — 8px, pinned top, z above nav */}
      <div className="fixed top-0 left-0 right-0 h-2 z-[60]" style={{ background: C.stripe }} />

      {/* nav */}
      <BlueWaveNav scrolled={scrolled} pathname={pathname} settings={siteSettings} />

      {/* hero */}
      <section className="relative overflow-hidden" style={{ background: C.grad, minHeight: '70vh' }}>
        {/* blobs */}
        <div className="bw-blob absolute -top-[18%] -right-[8%] w-[50vw] h-[50vw] opacity-70" style={{ background: 'radial-gradient(circle,#2E86FF 0%,#0A46E0 40%,transparent 70%)', filter: 'blur(100px)', animation: 'bwM 19s ease-in-out infinite, bwD 24s ease-in-out infinite' }} />
        <div className="bw-blob absolute -bottom-[12%] -left-[12%] w-[42vw] h-[42vw] opacity-70" style={{ background: 'radial-gradient(circle,#0B3DD0 0%,#061F8E 40%,transparent 70%)', filter: 'blur(110px)', animation: 'bwM 23s ease-in-out infinite reverse, bwD2 28s ease-in-out infinite' }} />
        <div className="bw-blob absolute top-[20%] left-[30%] w-[20vw] h-[20vw] opacity-30" style={{ background: 'radial-gradient(circle,rgba(252,209,22,0.3),transparent 70%)', filter: 'blur(90px)', animation: 'bwD 30s ease-in-out infinite reverse' }} />

        {/* content grid */}
        <div className="relative mx-auto max-w-[1440px]" style={{ padding: 'clamp(110px,10vw,132px) clamp(20px,5vw,80px) clamp(40px,5vw,60px)' }}>
          <div className="grid lg:grid-cols-[1.12fr_0.88fr] gap-10 lg:gap-[60px] items-center">

            {/* LEFT */}
            <div>
              {/* eyebrow */}
              <div className="bw-fu inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8" style={{ animationDelay: '.15s', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.24)' }}>
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: C.yellow }} />
                <span className="text-white/80 text-xs sm:text-sm font-medium uppercase tracking-[0.08em]">From Curaçao to the world stage</span>
              </div>

              {/* h1 — typewriter */}
              <h1 className="bw-fu font-heading font-semibold text-white mb-6" style={{ animationDelay: '.3s', fontSize: 'clamp(2.25rem,5vw,4.125rem)', lineHeight: 1.03, letterSpacing: '-0.025em', maxWidth: '18ch', minHeight: '3em' }}>
                {renderTyped()}
              </h1>

              {/* lead */}
              <p className="bw-fu mb-10 max-w-[46ch]" style={{ animationDelay: '.45s', color: 'rgba(255,255,255,0.84)', fontSize: 'clamp(1rem,1.4vw,1.19rem)', lineHeight: 1.6 }}>
                For 15 years we've helped leaders answer the questions that define their future. This year, our island answered one for the world.
              </p>

              {/* buttons */}
              <div className="bw-fu flex flex-col xs:flex-row flex-wrap gap-3" style={{ animationDelay: '.6s' }}>
                <Link to="/solutions" className="inline-flex items-center justify-center gap-2 font-bold text-sm rounded-full transition-transform hover:scale-105" style={{ padding: '12px 22px', background: '#fff', color: C.blue, boxShadow: '0 16px 36px rgba(3,16,70,0.28)', minHeight: 44 }}>
                  Ready to reshape? <ArrowRight size={16} />
                </Link>
                <Link to="/go-team-blue-wave" className="inline-flex items-center justify-center gap-2 font-bold text-sm text-white rounded-full transition-transform hover:scale-105" style={{ padding: '12px 22px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.4)', minHeight: 44 }}>
                  Ride the Blue Wave
                </Link>
              </div>
            </div>

            {/* RIGHT — stat */}
            <div className="flex items-center gap-5 lg:gap-6 mt-8 lg:mt-0">
              {/* vertical catalyst stripe */}
              <div className="w-2 rounded-full flex-shrink-0 self-stretch" style={{ background: C.vStripe, minHeight: 180 }} />

              <div>
                <div className="bw-fu font-heading font-bold text-white" style={{ animationDelay: '.35s', fontSize: 'clamp(5rem,11vw,9.5rem)', lineHeight: 0.86, letterSpacing: '-0.04em' }}>
                  156K
                </div>
                <p className="bw-fu mt-3 max-w-[30ch]" style={{ animationDelay: '.5s', color: 'rgba(255,255,255,0.84)', fontSize: 'clamp(0.85rem,1.1vw,1rem)', lineHeight: 1.55 }}>
                  the smallest population ever to reach a FIFA World Cup.
                </p>
                <div className="h-px bg-white/15 my-4 max-w-[260px]" />
                <p className="bw-fu font-heading font-semibold" style={{ animationDelay: '.65s', color: C.yellow, fontSize: 'clamp(1rem,1.5vw,1.375rem)', letterSpacing: '-0.01em' }}>
                  Momentum is built, not given.
                </p>
              </div>
            </div>

          </div>
        </div>
        {/* Bottom wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 60" fill="none" preserveAspectRatio="none" className="w-full block" style={{ height: 'clamp(30px, 4vw, 60px)' }}>
          <path d="M0 30C180 55 360 5 540 25C720 45 900 10 1080 30C1200 45 1350 15 1440 25V60H0Z" fill="white"/>
        </svg>
      </div>
    </section>
    </>
  );
};

export default BlueWaveHero;
